/** Derive Quality Scorecard aggregates from scored metric rows (no static demo data). */

import { TRUST_ATTRIBUTES } from "../../config/trustAttributes";
import {
  formatActualForScorecard,
  formatExpectedForScorecard,
} from "./gate2MetricFormat";

function thresholdPercent(threshold, unit) {
  if (threshold == null || !Number.isFinite(Number(threshold))) return null;
  const n = Number(threshold);
  if (unit === "fraction" || unit === "rate") return n > 1 ? n : n * 100;
  return n;
}

function actualPercent(value, unit) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const n = Number(value);
  if (unit === "fraction" || unit === "rate") return n > 1 ? n : n * 100;
  return n;
}

/** Figma-style blocking lines from failed metrics + cases not executed in this run. */
export function buildScorecardBlockingLines(results, executionCases = []) {
  const lines = [];
  const rows = Array.isArray(results) ? results : [];

  for (const row of rows) {
    if (row.passed !== false || row.value == null || row.error) continue;
    const actualStr = formatActualForScorecard(row);
    const threshPct = thresholdPercent(row.threshold, row.unit);
    const actualPct = actualPercent(row.value, row.unit);
    const expected = formatExpectedForScorecard(row.threshold, row.direction, row.unit);
    const thresholdLabel =
      threshPct != null
        ? `${threshPct % 1 === 0 ? threshPct.toFixed(0) : threshPct.toFixed(1)}%`
        : expected.replace(/^[≤<>=]+ /, "").trim();

    if (actualPct != null && threshPct != null) {
      const pp = Math.abs(actualPct - threshPct).toFixed(1);
      lines.push(
        `${row.metricName} ${actualStr} - exceeds ${thresholdLabel} threshold by ${pp}pp`
      );
    } else {
      lines.push(`${row.metricName} ${actualStr} - below target (${expected})`);
    }
  }

  const cases = Array.isArray(executionCases) ? executionCases : [];
  for (const tc of cases) {
    const retrieved =
      tc.outcome === "retrieved" ||
      Boolean(String(tc.answer || "").trim()) ||
      (tc.chunks || []).some((ch) => ch.content) ||
      (tc.traces || []).length > 0;
    if (retrieved) continue;
    const code = tc.code ? String(tc.code) : tc.id ? `TC-${String(tc.id).slice(0, 8)}` : "Test case";
    const label = tc.name || tc.metricName || "validation";
    lines.push(`${code} not yet executed (${label})`);
  }

  return lines;
}

/** Same scored-row rule the scorecard already uses. Pass/fail stays on `passed`. */
function isScoredMetricRow(row) {
  return Boolean(row?.metricName) && row.value != null && !row.error && typeof row.passed === "boolean";
}

/**
 * Register row for a scored result. Every metric has a trust attribute.
 * Metric-scoped jobs use the register id as `key`; suite jobs use catalogue id.
 */
function registerMetricForResult(row, metrics) {
  const list = Array.isArray(metrics) ? metrics : [];
  const byId = list.find((metric) => metric?.id != null && String(metric.id) === String(row?.key));
  if (byId) return byId;
  const byCatalog = list.find((metric) => metric?.metricId && metric.metricId === row?.metricId);
  if (byCatalog) return byCatalog;
  return list.find((metric) => metric?.metricName && metric.metricName === row?.metricName) || null;
}

/**
 * Gate 2 Decision trust score.
 *
 * Each scored metric is 1 (pass) or 0 (fail). Metrics in one trust attribute
 * are averaged, then multiplied by that attribute's Gate 1 weight (a percent).
 * An attribute with no scored metrics contributes 0. Its weight is not given
 * to the other attributes. Custom test cases are not votes; callers pass
 * metric results only.
 *
 * Returns null when Gate 1 weights were never loaded. The score is 0 when
 * weights are present and nothing is scored.
 *
 * `trustScoreBreakdown` is the same calculation plus the per-attribute figures
 * the backend terminal prints. The donut uses `weightedTrustScore`, which is
 * only the rounded number.
 */
export function trustScoreBreakdown(results, metrics, trustWeights) {
  if (!trustWeights || typeof trustWeights !== "object") return null;

  const scored = (Array.isArray(results) ? results : []).filter(isScoredMetricRow);
  const attributes = [];
  let unrounded = 0;
  for (const attribute of TRUST_ATTRIBUTES) {
    const weight = Number(trustWeights[attribute]);
    const inAttribute = scored.filter(
      (row) => registerMetricForResult(row, metrics)?.trustAttribute === attribute
    );
    const passedCount = inAttribute.filter((row) => row.passed === true).length;
    const scoredCount = inAttribute.length;
    const passRate = scoredCount === 0 ? 0 : passedCount / scoredCount;
    const contribution = weight * passRate;
    unrounded += contribution;
    attributes.push({
      attribute,
      weight,
      passedCount,
      scoredCount,
      passRate,
      contribution,
    });
  }
  return {
    trustWeights,
    attributes,
    unrounded,
    score: Math.round(unrounded),
  };
}

export function weightedTrustScore(results, metrics, trustWeights) {
  const breakdown = trustScoreBreakdown(results, metrics, trustWeights);
  return breakdown ? breakdown.score : null;
}

export function buildScorecardSummary(results, targetTrustIndex) {
  const rows = Array.isArray(results) ? results.filter((r) => r?.metricName) : [];
  const evaluated = rows.filter(isScoredMetricRow);
  const passed = evaluated.filter((r) => r.passed === true);
  const failed = evaluated.filter((r) => r.passed === false);
  const total = rows.length;
  const trustScore =
    evaluated.length > 0 ? Math.round((passed.length / evaluated.length) * 100) : null;
  const target =
    targetTrustIndex != null && Number.isFinite(Number(targetTrustIndex))
      ? Number(targetTrustIndex)
      : null;
  const meetsTarget = trustScore != null && target != null ? trustScore >= target : null;
  const qualityGateFailed = failed.length > 0;

  return {
    rows,
    evaluated,
    passed,
    failed,
    total,
    passedCount: passed.length,
    failedCount: failed.length,
    evaluatedCount: evaluated.length,
    trustScore,
    target,
    meetsTarget,
    qualityGateFailed,
  };
}

/** Split Gate 1 risks for the summary footer from live register data. */
export function partitionRisksForScorecard(risks) {
  const list = Array.isArray(risks) ? risks.filter((r) => r?.enabled !== false) : [];
  const blocking = list.filter((r) => r.severity === "Critical" || r.severity === "High");
  const mitigated = list.filter((r) => r.severity === "Medium" || r.severity === "Low");
  return { blocking, mitigated };
}
